import { mapObject } from "services";
import {
  formatNumberToMoney,
  formatNumberToMoneyCurrency,
  parseMoneyToNumber,
} from "services/money.service";
import { questionResponse } from "./question.mapper";

// SVEMS-774 — Guest Profile Discount. Every read and write of the API field goes
// through this single constant. BE confirmed the contract field name is "discount"
// on 2026-08-20; no other file names the API field.
const GUEST_PROFILE_DISCOUNT_KEY = "discount";

// A percentage the form accepts as 1-100. BE confirmed on 2026-08-20 that it is decimal,
// not whole-percent, and that null and 0 mean the same thing — so missing, null, 0 and
// unparsable all collapse to "no discount", which is submitted as 0 and shown as nothing
// at all rather than as 0%.
//
// Parsing goes through parseMoneyToNumber, not Number(), because the form's own validators
// parse with it too. Number() would disagree with them on a locale decimal comma: in es/pt,
// "12,5" passes validation as 12.5 but Number("12,5") is NaN, so the save would silently
// write 0. parseMoneyToNumber returns Number(val) unchanged for an already-numeric value,
// so BE responses are unaffected.
const parseProfileDiscount = (value) => {
  const parsed = parseMoneyToNumber(value).value;
  return Number.isFinite(parsed) ? parsed : 0;
};

export const guestProfileResponse = {
  id: {
    key: "guestProfileId",
  },
  name: {
    key: "guestProfileName",
  },
  amount: {
    transform: ({ src }) => {
      return src.amount ? formatNumberToMoneyCurrency(src.amount) : "-";
    },
  },
  freeCredit: {
    transform: ({ src }) => {
      return src.freeCredit ? formatNumberToMoneyCurrency(src.freeCredit) : "-";
    },
  },
  postpaid: {
    transform: ({ src }) => {
      return src.postpaidCreditLimit ? formatNumberToMoneyCurrency(src.postpaidCreditLimit) : "-";
    },
  },
  profileDiscount: {
    transform: ({ src }) => {
      const discount = parseProfileDiscount(src[GUEST_PROFILE_DISCOUNT_KEY]);
      // A dash for no discount, the way this table's Free Credits, Postpaid Credits and
      // Preloaded Vouchers columns already render an absent value.
      return discount ? `${discount}%` : "-";
    },
  },
  preloaded: {
    transform: ({ src }) => {
      if (src.guestProfileVouchers) {
        return src.guestProfileVouchers.map((gp) => {
          return `${gp.quantity} x ${gp?.voucher?.voucherName || "-"}`;
        });
      }
      return [];
    },
  },
  vouchers: {
    transform: ({ src }) => {
      if (src.guestProfileVouchers) {
        return src.guestProfileVouchers.map(({ quantity, voucher }) => {
          return {
            value: voucher.voucherId,
            quantity,
            name: voucher.voucherName,
          };
        });
      }
      return [];
    },
  },
  isTagRegistration: {
    key: "isTagRegistration",
  },
};

export const guestProfileIdResponse = {
  _keys: ["description", "guestProfileDefault"],
  id: {
    key: "guestProfileId",
  },
  name: {
    key: "guestProfileName",
  },
  eatAppRoomId: {
    key: "eatAppId",
  },
  vouchers: {
    transform: ({ src }) => {
      if (src.guestProfileVouchers) {
        return src.guestProfileVouchers.map(({ quantity, voucher }) => {
          return {
            value: voucher.voucherId,
            quantity,
            name: voucher.voucherName,
          };
        });
      }
      return [];
    },
  },
  questions: {
    transform: ({ src }) => {
      const obj = {};
      [
        "firstNameRequired",
        "lastNameRequired",
        "emailRequired",
        "mobileRequired",
        "birthdayRequired",
        "genderRequired",
        "customQuestions",        
        "emailIncluded",
        "mobileIncluded",
        "birthdayIncluded",
        "genderIncluded",
      ].forEach((key) => {
        if (src[key]) {
          obj[key] = true;
        }
      });
      return obj;
    },
  },
  defaultQuestionIncludes: {
    transform: ({ src }) => {
      const obj = {};
      [
        "emailIncluded",
        "mobileIncluded",
        "birthdayIncluded",
        "genderIncluded",
      ].forEach((key) => {
        if (src[key]) {
          obj[key] = true;
        }
      });
      return obj;
    },
  },
  isAllowedPostLimit: {
    transform: ({ src }) => {
      if (!src.postpaidAllowed) {
        return false;
      }
      return true;
    },
  },
  isAskQuestion: {
    transform: ({ src }) => {
      if (!src.questionnaireActive) {
        return false;
      }
      return true;
    },
  },
  customQuestionIncludes: {
    transform: ({ src }) => {
      const obj = {};
      if (src.guestProfileQuestions) {
        src.guestProfileQuestions.forEach(({ question }) => {
          if (question?.questionId) {
            obj[question.questionId] = question;
          }
        });
      }
      return obj;
    },
  },
  customQuestionsRequired: {
    transform: ({ src }) => {
      const obj = {};
      if (src.guestProfileQuestions) {
        src.guestProfileQuestions.forEach(({ question, required }) => {
          if (required) {
            obj[question.questionId] = question;
          }
        });
      }

      return obj;
    },
  },
  sortQuestions: {
    transform: ({ src }) => {
      if (src.guestProfileQuestions) {
        return src.guestProfileQuestions.map(({ question }) => {
          return mapObject(question, questionResponse);
        });
      }
      return [];
    },
  },
  limit: {
    transform: ({ src }) => {
      return formatNumberToMoney(src.postpaidCreditLimit);
    },
  },
  freeCredit: {
    transform: ({ src }) => {
      return formatNumberToMoney(src.freeCredit);
    },
  },
  amount: {
    transform: ({ src }) => {
      return formatNumberToMoney(src.amount);
    },
  },
  profileDiscount: {
    transform: ({ src }) => {
      const discount = parseProfileDiscount(src[GUEST_PROFILE_DISCOUNT_KEY]);
      // Empty, not 0 — this one feeds the form input, and a literal 0 there is now a
      // validation error. Empty submits as 0, which is what BE means by no discount.
      return discount ? discount : "";
    },
  },
  isTagRegistration: {
    key: "isTagRegistration",
  }
};

export const guestProfileRequest = {
  _keys: [
    "description",
    "venueId",
    "firstNameRequired",
    "lastNameRequired",
    "emailRequired",
    "mobileRequired",
    "birthdayRequired",
    "genderRequired",
    "customQuestions",
    "topupCreditLimit",
    "topupCreditLimited",
    "emailIncluded",
    "mobileIncluded",
    "birthdayIncluded",
    "genderIncluded",
    "isTagRegistration"
  ],
  guestProfileName: {
    key: "name",
  },
  description: {
    key: "description",
  },
  postpaidAllowed: {
    key: "isAllowedPostLimit",
  },
  postpaidCreditLimit: {
    transform: ({ src }) => {
      return parseMoneyToNumber(src.limit).value;
    },
  },
  amount: {
    transform: ({ src }) => {
      return parseMoneyToNumber(src.amount).value;
    },
  },
  freeCredit: {
    transform: ({ src }) => {
      return parseMoneyToNumber(src.freeCredit).value;
    },
  },
  [GUEST_PROFILE_DISCOUNT_KEY]: {
    transform: ({ src }) => {
      return parseProfileDiscount(src.profileDiscount);
    },
  },
  questionnaireActive: {
    key: "isAskQuestion",
  },
  vouchers: {
    transform: ({ self }) => {
      return self.map((voucher) => {
        return {
          voucherId: voucher.value,
          quantity: voucher.quantity,
        };
      });
    },
  },
};

export const editGuestProfileRequest = {
  ...guestProfileRequest,
  guestProfileId: {
    key: "guestProfileId",
  },
};

export const guestProfileFilterRequest = {
  includeAppliedGuestProfile: true,
  orders: { "gp.createdDate": "desc", "gp.guestProfileDefault": "desc" },
  venueId: { key: "venueId" },
  includeDeleted: { key: "includeDeleted" },
};

export const guestProfileFilterResponse = {
  id: { key: "guestProfileId" },
  name: {
    transform: ({ src }) => {
      return src.guestProfileName + (src.deleted ? " (deleted)" : "");
    },
  },
};
